/**
 * Live notifications (D-084, FigCraft page 50): the unread count behind every tab's bell, kept current from the socket,
 * and a pop-up that slides down for each new notice with a View action. Table invites keep their own live card
 * (TableInviteCard), so they count on the bell but don't pop up twice. The text and link are the server's stored
 * notification, so the pop-up and the list always say the same thing.
 * D-203: a notice that arrives while the app is open plays a chime (never the ones already there on load): a soft
 * notify, the celebrate run for a friend who said yes, and the turn-style call when your tournament table is ready,
 * whose pop-up then says "Go to your table".
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { WsServerMessage } from "@digu/shared";
import { api } from "./api";
import { onNotify } from "./ws";
import { useSessionRefresh } from "./session";
import type { NotificationsView } from "./types";
import { Icon } from "../components/Icon";
import { CelebrateBurst } from "../components/Celebrate";
import { WatchNoticeActions } from "../components/WatchNoticeActions";
import { isWatchNotice, OFFER_MS } from "./noticeActions";
import { chime, type Chime } from "./chimes";

type Item = NotificationsView["items"][number];
type NotifyMessage = Extract<WsServerMessage, { t: "notify" }>;
interface Ctx { unread: number; refresh: () => void; markedRead: () => void }
const NotificationsContext = createContext<Ctx>({ unread: 0, refresh: () => undefined, markedRead: () => undefined });

export const useNotifications = (): Ctx => useContext(NotificationsContext);

/** How long a pop-up stays before it slides away on its own. */
export const POPUP_MS = 6000;

/**
 * What a live notice sounds like, from its socket message (the stored item has only its text): the call for a
 * tournament table that is ready (the engine's "seated" and "final", tournament-engine.ts), the celebrate run for an
 * accepted friend request, the soft notify for everything else.
 */
export function noticeChime(m: Pick<NotifyMessage, "kind" | "payload"> | null | undefined): Chime {
  const sub = m && typeof m.payload === "object" && m.payload !== null ? (m.payload as { kind?: unknown }).kind : undefined;
  if (m?.kind === "tournament" && (sub === "seated" || sub === "final")) return "call";
  if (m?.kind === "info" && sub === "friend-accepted") return "celebrate";
  return "notify";
}

export function NotificationsProvider({ enabled, children }: { enabled: boolean; children: ReactNode }): ReactElement {
  const [unread, setUnread] = useState(0);
  const [popup, setPopup] = useState<{ item: Item; tone: Chime } | null>(null);
  const seen = useRef<Set<string> | null>(null);
  const refreshSession = useSessionRefresh();
  const load = useCallback(async (announce: boolean, m?: NotifyMessage) => {
    try {
      const r = await api.get<NotificationsView>("/notifications");
      setUnread(r.items.filter((n) => !n.read).length);
      const known = seen.current;
      seen.current = new Set(r.items.map((n) => n.id));
      if (!announce || !known) return;
      const arrived = r.items.filter((n) => !n.read && !known.has(n.id));
      if (arrived.length === 0) return;
      const tone = noticeChime(m);
      chime(tone);
      const fresh = arrived.find((n) => n.kind !== "table-invite");
      if (fresh) setPopup({ item: fresh, tone });
    } catch { /* the bell keeps its last count */ }
  }, []);
  useEffect(() => {
    if (!enabled) { setUnread(0); seen.current = null; return; }
    void load(false);
    // D-193: a new friend or club request also moves the counts on the Friends and Clubs tabs (they come with the session).
    const off = onNotify((m) => { void load(true, m); if (m?.kind === "friend-request" || m?.kind === "club-request") void refreshSession(); });
    const onFocus = () => { void load(false); };
    window.addEventListener("focus", onFocus);
    return () => { off(); window.removeEventListener("focus", onFocus); };
  }, [enabled, load]); // eslint-disable-line react-hooks/exhaustive-deps
  // A seat offer stays up while the seat is held (D-120); everything else slides away after a few seconds.
  useEffect(() => { if (!popup) return; const t = setTimeout(() => setPopup(null), popup.item.kind === "seat-offer" ? Math.max(0, popup.item.at + OFFER_MS - Date.now()) : POPUP_MS); return () => clearTimeout(t); }, [popup]);
  const value: Ctx = { unread, refresh: () => { void load(false); }, markedRead: () => setUnread(0) };
  return (
    <NotificationsContext.Provider value={value}>
      {children}
      {popup && <NotificationPopup item={popup.item} tone={popup.tone} onClose={() => setPopup(null)} />}
    </NotificationsContext.Provider>
  );
}

function NotificationPopup({ item, tone, onClose }: { item: Item; tone: Chime; onClose: () => void }): ReactElement {
  const navigate = useNavigate();
  const icon = tone === "call" ? "trophy" : tone === "celebrate" ? "user-plus" : "bell";
  return (
    <div className={`notif-pop${tone === "call" ? " notif-pop--call" : ""}`} role="status" aria-live="polite" data-testid="notification-popup">
      <span className="notif-pop__icon" aria-hidden="true">{tone === "celebrate" ? <CelebrateBurst icon={icon} /> : <Icon name={icon} size={22} />}</span>
      <span className="notif-pop__text">{item.text}<span className="notif-pop__time">Just now</span></span>
      {isWatchNotice(item.kind)
        ? <WatchNoticeActions item={item} onGo={(to) => { onClose(); navigate(to); }} className="notif-pop__acts" />
        : item.href && <button type="button" className="notif-pop__view" onClick={() => { onClose(); navigate(item.href as string); }}>{tone === "call" ? "Go to your table" : "View"}</button>}
      <button type="button" className="notif-pop__close" aria-label="Dismiss" onClick={onClose}><Icon name="x" size={18} /></button>
    </div>
  );
}
